import { Box, FormGroup, Grid, Stack, Typography } from '@mui/material';
import PortalModal from '../../common/PortalModal';
import { FormTextField } from '../../common/Form/FormTextField';
import { useEffect, useState } from 'react';
import { WithSmallScreen } from '../../common/WithSmallScreen';
import { FormTimePicker } from '../../common/Form/FormTimePicker';
import PortalTooltip from '../../common/PortalTooltip';
import { FormDatePicker } from '../../common/Form/FormDatePicker';
import dayjs from 'dayjs';
import { Time } from '../../common/MomentTime';
import { FormCheckbox } from '../../common/Form/FormCheckbox';
import { FormAutoComplete } from '../../common/Form/FormAutoComplete';
import { getDefaultAdhocStartEndTime } from '../../MyCourses/CCT/Body/Zoom/ScheduleMeeting/utils';

export const AdhocMeetingModal = WithSmallScreen(
  ({ isOpen, toggleModalOpen, id, onConfirm, course, codecs = [], isSmall = false, ...props }) => {
    const [meetingTitle, setMeetingTitle] = useState(
      course.TERM_CODE + '_' + course.SUBJECT_CODE + '_' + course.COURSE_NUMBER + '_' + course.CRN,
    );
    const [date, setDate] = useState(
      Time.University().diff(Time.University(course.START_DATE), 'days') < 0
        ? dayjs(course.START_DATE)
        : Time.University(null, true),
    );
    const [startTime, setStartTime] = useState();
    const [endTime, setEndTime] = useState();
    const [isRecordMeeting, setIsRecordMeeting] = useState(true);
    const [isEnableWaitingRoom, setIsEnableWaitingRoom] = useState(true);
    const [isAddCodecs, setIsAddCodecs] = useState(false);
    const [selectedCodecs, setSelectedCodecs] = useState([]);
    const isEndTimeError = endTime?.diff(startTime, 'minute') <= 0;
    const checkIsErrors = () => {
      return !meetingTitle?.trim().length || endTime?.diff(startTime, 'minute') <= 0;
    };

    useEffect(() => {
      const { startTime, endTime } = getDefaultAdhocStartEndTime();
      setStartTime(dayjs(startTime));
      setEndTime(dayjs(endTime));
    }, []);

    useEffect(() => {
      if (!isAddCodecs) {
        setSelectedCodecs([]);
      }
    }, [isAddCodecs]);

    return (
      <PortalModal
        title={'Adhoc Meeting'}
        maxWidth="md"
        isOpen={isOpen}
        onClose={toggleModalOpen}
        isCloseable
        isFullScreen={isSmall}
        primaryButtonTitle="Add"
        isPrimaryButtonDisabled={checkIsErrors()}
        onPrimaryClick={() =>
          onConfirm({
            meetingTitle,
            date,
            startTime,
            endTime,
            isRecordMeeting,
            isEnableWaitingRoom,
            codecs: isAddCodecs
              ? {
                  startDate: date,
                  endDate: date,
                  codecsIds: selectedCodecs.map((codec) => codec.ID),
                }
              : undefined,
          })
        }
        secondaryButtonTitle="Cancel"
        onSecondaryClick={toggleModalOpen}
        id={`${id}__modal`}
      >
        <Grid
          id={`${id}__grid_container`}
          container
          direction="column"
          spacing={2}
          sx={{
            justifyContent: 'center',
          }}
        >
          <Grid item id={`${id}__grid_item_title`} xs={12} xl={3}>
            <FormTextField
              id={`${id}__inputText_title`}
              label="Meeting Title"
              onChange={setMeetingTitle}
              value={meetingTitle}
              name="meetingTitle"
              inputGridProps={{ xs: 12, md: 10 }}
              labelGridProps={{ xs: 12, md: 2 }}
              textAlign={{ xs: 'start', md: 'end' }}
              required
              error={meetingTitle?.trim().length === 0}
              errorText={'Title Required'}
            />
          </Grid>
          <Grid item id={`${id}__grid_item_date`} xs={12} xl={3}>
            <FormDatePicker
              id={`${id}__datePicker_date`}
              value={date}
              onChange={setDate}
              label={'Date'}
              inputGridProps={{ xs: 12, md: 4 }}
              labelGridProps={{ xs: 12, md: 2 }}
              minDate={dayjs(course?.START_DATE)}
            />
          </Grid>
          <Grid item id={`${id}__grid_item_time`} xs={12} xl={3}>
            <Grid container id={`${id}__grid_container_time`} spacing={{ xs: 0, sm: 1 }}>
              <Grid
                item
                id={`${id}__grid_item_label_time`}
                xs={12}
                md={2}
                textAlign={{ xs: 'start', md: 'end' }}
                style={{
                  paddingTop: props.isSmall ? 8 : 16,
                }}
              >
                <Typography id={`${id}__text_label_time`} className="myOdu__label">
                  Time
                </Typography>
              </Grid>
              <Grid
                item
                id={`${id}__grid_item_timePicker`}
                container
                xs={12}
                md={10}
                wrap="nowrap"
                spacing={1}
              >
                <Grid item xs={6} md={3}>
                  <FormTimePicker
                    id={`${id}__timePicker_startTime`}
                    name={'customStartTime'}
                    value={startTime}
                    onChange={setStartTime}
                    inputGridProps={{ xs: 12 }}
                    labelGridProps={{ xs: 0 }}
                    helperText={
                      <PortalTooltip
                        title={
                          "Meetings are always scheduled in ODU's timezone which changes between Eastern Standard Time and Eastern Daylight Time"
                        }
                        id={`${id}__tooltip_universityTimezone`}
                      >
                        <Typography
                          style={{
                            cursor: 'pointer',
                          }}
                          variant="p"
                          className="myOdu__universityTime"
                          id={`${id}__text_universityTimezone`}
                        >
                          University Timezone
                        </Typography>
                      </PortalTooltip>
                    }
                  />
                </Grid>
                <Grid
                  item
                  xs={'auto'}
                  textAlign={'center'}
                  style={{
                    paddingTop: props.isSmall ? 8 : 16,
                  }}
                  id={`${id}__grid_item_time_to`}
                >
                  <Typography variant="p" id={`${id}__text_label_to`}>
                    To
                  </Typography>
                </Grid>
                <Grid item id={`${id}_grid_item_time_to_timePicker`} xs={6} md={3}>
                  <FormTimePicker
                    id={`${id}_timePicker_endTime`}
                    name={'customEndTime'}
                    value={endTime}
                    onChange={setEndTime}
                    inputGridProps={{ xs: 12 }}
                    error={isEndTimeError}
                    errorText={'End time must be after start time'}
                  />
                </Grid>
              </Grid>
            </Grid>
          </Grid>
          <Grid item id={`${id}_grid_item_options`} xs={12} xl={3}>
            <Grid container id={`${id}_grid_container_options`} spacing={{ xs: 0, sm: 1 }}>
              <Grid
                item
                id={`${id}_grid_item_label_options`}
                xs={12}
                md={2}
                textAlign={{ xs: 'start', md: 'end' }}
              >
                <Typography id={`${id}_text_label_options`} className="myOdu__label">
                  Options
                </Typography>
              </Grid>
              <Grid
                item
                id={`${id}_grid_item_options_checkbox`}
                xs={12}
                md={10}
                alignItems="center"
              >
                <FormCheckbox
                  label={'Record Meeting'}
                  id={`${id}_options_checkbox_recordMeeting`}
                  onChange={(event) => setIsRecordMeeting(event.target.checked)}
                  name={'isRecordMeeting'}
                  labelGridProps={{
                    xs: 0,
                  }}
                  inputGridProps={{
                    xs: 12,
                    md: 9,
                    lg: 8,
                    xl: 9,
                  }}
                  value={isRecordMeeting}
                  sx={{ marginRight: '0.25rem' }}
                />
                <FormCheckbox
                  label={'Enable Waiting Room (Recommended for office hours)'}
                  id={`${id}_options_checkbox_waitingRoom`}
                  onChange={(event) => setIsEnableWaitingRoom(event.target.checked)}
                  name={'isEnableWaitingRoom'}
                  labelGridProps={{
                    xs: 0,
                  }}
                  inputGridProps={{
                    xs: 12,
                    md: 9,
                    lg: 8,
                    xl: 9,
                  }}
                  value={isEnableWaitingRoom}
                  sx={{ marginRight: '0.25rem' }}
                />
                <FormCheckbox
                  label={'Add Codecs'}
                  id={`${id}_options_checkbox_addCodecs`}
                  onChange={(event) => setIsAddCodecs(event.target.checked)}
                  name={'isAddCodecs'}
                  labelGridProps={{
                    xs: 0,
                  }}
                  inputGridProps={{
                    xs: 12,
                    md: 9,
                    lg: 8,
                    xl: 9,
                  }}
                  value={isAddCodecs}
                  sx={{ marginRight: '0.25rem' }}
                />
                {isAddCodecs && (
                  <Grid container id={`${id}_grid_container_addCodecs`} spacing={{ xs: 0, sm: 1 }}>
                    <Grid item id={`${id}_grid_item_codecs`} xs={12} md={10}>
                      <FormAutoComplete
                        value={selectedCodecs}
                        id={`${id}_selectAutoComplete_codecs`}
                        label="Codecs"
                        labelGridProps={{
                          xs: 12,
                          md: 2,
                        }}
                        inputGridProps={{
                          xs: 12,
                          md: 10,
                        }}
                        options={codecs}
                        getOptionLabel={(option) => option.SYSTEM_NAME}
                        placeholder="Search by System Name"
                        onChange={(e, value) => setSelectedCodecs(value)}
                        autocompleteProps={{
                          multiple: true,
                        }}
                        ariaLabel="Codecs Selection"
                      />
                    </Grid>
                    <Grid item id={`${id}_grid_item_codecs_date`} xs={12} md={10}>
                      <FormDatePicker
                        value={date}
                        id={`${id}_codecs_datePicker_date`}
                        label={'Date'}
                        labelGridProps={{
                          xs: 12,
                          md: 2,
                        }}
                        disabled={true}
                        inputGridProps={{
                          xs: 12,
                          md: 10,
                        }}
                      />
                    </Grid>
                  </Grid>
                )}
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </PortalModal>
    );
  },
);
